import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import InlineSVG from "react-inlinesvg";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { MenuPopoverItem } from "src/sudokit/components/MenuPopover";
import { useProjectStore } from "../../../../stores/projectStore";
import { useSeriesStore } from "../../../../stores/seriesStore";
import { useStoryBibleStore } from "../../../../stores/storyBibleStore";
import { getIn } from "../../../../Utils";
import { PROSE_MODES } from "../../../StoryBible";
import { useStructuredOutlineStore } from "../../../StoryBible/StructuredOutline/StructuredOutlineStore";

// Tailwind overrides to match the old .scene styled-component styles
const sceneItemStyles = "hover:bg-[var(--primary-cta-transparent)]";

// This is called ChapterSelector, but it includes options to add a new folder or a new document
export default function ChapterSelector({ project, setDocumentId, addNewDocument, closeMenu }) {
  const options = useStoryBibleStore((state) => state.options);
  const outlineObject = useStoryBibleStore((state) => state.outlineObject);
  const createScene = useStoryBibleStore((state) => state.createScene);
  const setProseGeneratorActive = useStoryBibleStore((state) => state.setProseGeneratorActive);
  const scenes = useStoryBibleStore((state) => state.scenes);
  const saved = useStoryBibleStore((state) => state.saved);
  const createStructureOutlineScene = useStoryBibleStore((state) => state.createStructureOutlineScene);
  const setScenes = useStoryBibleStore((state) => state.setScenes);
  const creatingDocument = useProjectStore((state) => state.creatingDocument);
  const setCreatingDocument = useProjectStore((state) => state.setCreatingDocument);
  const buildChapterOptions = useStructuredOutlineStore((state) => state.buildChapterOptions);
  const selectedProjects = useSeriesStore((state) => state.selectedProjects);
  const subscription = useSudowriteStore((state) => state.subscription);
  const getDraftDefaults = useSudowriteStore((state) => state.getDraftDefaults);
  const [newDocumentId, setNewDocumentId] = useState();

  useEffect(() => {
    if (!newDocumentId) return;
    const newScene = getIn(scenes, [(i) => i?.contents?.id === newDocumentId]);
    if (newScene.length > 0 && saved) {
      // Apply defaults if they weren't set yet (for linked chapters created via + New menu)
      try {
        const { modelKey, creativity } = getDraftDefaults(project?.id);
        if ((modelKey && !newScene[0].proseMode) || (typeof creativity === "number" && newScene[0].creativityLevel === undefined)) {
          setScenes((draft) => {
            const sceneRef = draft[newScene[0].id];
            if (!sceneRef) return;
            if (modelKey && PROSE_MODES?.[modelKey] && !sceneRef.proseMode) {
              sceneRef.proseMode = modelKey;
            }
            if (typeof creativity === "number" && sceneRef.creativityLevel === undefined) {
              sceneRef.creativityLevel = creativity;
            }
          });
        }
      } catch (err) {
        Sentry.captureException(err, { tags: { projectId: project?.id || "unknown", feature: "chapter_selector_apply_defaults" } });
      }
      setDocumentId(newDocumentId);
      subscription?.hasAccessToChapterGlueBeta ? setProseGeneratorActive(false) : setProseGeneratorActive(true);
      setCreatingDocument(false);
      setNewDocumentId(null);
      closeMenu();
    }
  }, [newDocumentId, scenes, saved]);

  const onClick = async (selectValue) => {
    const selectedSceneIndex = selectValue.value;
    const [selectedSection, selectedScene] = outlineObject.sectionAndsceneAtIndex(selectedSceneIndex);
    const title = selectValue.label;
    if (creatingDocument) {
      return;
    } else {
      setCreatingDocument(true);

      const result = await project.createDocument({
        title,
      });
      createScene(result.document.id, selectedSection, selectedScene);
      setCreatingDocument(false);
      setNewDocumentId(result.document.id);
    }
  };

  const handleStructuredAdd = async (selectValue) => {
    if (creatingDocument) return;
    setCreatingDocument(true);

    try {
      const chapters = buildChapterOptions();
      const previousDocumentId = subscription?.hasAccessToChapterGlueBeta
        ? await project.getPreviousDocumentIdFromChapter({
            chapterId: selectValue.value,
            chapters,
            scenes,
            userId: subscription.id,
          })
        : null;

      const selectedChapter = chapters.find((chapter) => chapter.value === selectValue.value);
      const result = await project.createDocument({
        title: selectValue.label,
        previousDocumentId,
        povTenseId: selectedChapter?.povTenseId,
      });
      const newSceneId = createStructureOutlineScene(result.document.id, selectValue.value);

      const { modelKey, creativity } = getDraftDefaults(project?.id);
      if (modelKey || typeof creativity === "number") {
        setScenes((draft) => {
          if (!draft[newSceneId]) return;
          if (modelKey && PROSE_MODES?.[modelKey]) {
            draft[newSceneId].proseMode = modelKey;
          }
          if (typeof creativity === "number") {
            draft[newSceneId].creativityLevel = creativity;
          }
        });
      }
      setCreatingDocument(false);
      setNewDocumentId(result.document.id);
    } catch (err) {
      Sentry.captureException(err, { tags: { projectId: project?.id || "unknown", feature: "chapter_selector_create_linked_chapter" } });
      setCreatingDocument(false);
    }
  };

  const showLegacyOutlineOptions = !project.useStructuredOutline;
  const showStructuredOutlineOptions = project.useStructuredOutline;
  const displayChapterOptions = !project.seriesId || selectedProjects.outline?.projectId === project.id;
  const structuredOptions = displayChapterOptions ? buildChapterOptions() : [];

  return (
    <>
      <MenuPopoverItem
        className={sceneItemStyles}
        onSelect={() => {
          const currentMaxDisplayOrder = project.getMaxDisplayOrder("none");
          project.createFolder("New Folder", undefined, currentMaxDisplayOrder + 1);
        }}
      >
        <InlineSVG src="/assets/single-folder.svg" className="size-auto" aria-hidden="true" />
        <span className="truncate">Folder</span>
      </MenuPopoverItem>
      <MenuPopoverItem
        className={sceneItemStyles}
        onSelect={() => {
          addNewDocument();
        }}
      >
        <InlineSVG src="/assets/document.svg" className="size-auto" aria-hidden="true" />
        <span className="truncate">Blank document</span>
      </MenuPopoverItem>
      {showLegacyOutlineOptions &&
        options?.length > 0 &&
        Object.values(options).map((section, sectionIndex) =>
          Object.values(section.options).map((scene, sceneIndex) => (
            <MenuPopoverItem
              key={`${sectionIndex}-${sceneIndex}`}
              className={sceneItemStyles}
              onSelect={() => onClick(scene)}
              closeOnSelect={false}
            >
              <InlineSVG src="/assets/document.svg" className="size-auto" aria-hidden="true" />
              <span className="truncate">{scene.label}</span>
            </MenuPopoverItem>
          )),
        )}
      {showStructuredOutlineOptions &&
        structuredOptions.map((option) => (
          <MenuPopoverItem
            key={option.value}
            className={sceneItemStyles}
            onSelect={() => handleStructuredAdd(option)}
            closeOnSelect={false}
          >
            <InlineSVG src="/assets/document.svg" className="size-auto" aria-hidden="true" />
            <span className="truncate">{option.label}</span>
          </MenuPopoverItem>
        ))}
    </>
  );
}
